import React, { useMemo } from 'react';
import { Accordion, Button, Highlight, Text } from '@mantine/core';
import { IconUserPlus } from '@tabler/icons-react';
import OpsLeaderCard from './OpsLeaderCard';
import { useOrganisationSearch } from '../../context/OrganisationSearchContext';
import ProcurementManagers from './ProcurementManagers';
import { ProcurementExecutive } from '../../codegen/graphql';
import { PropagationStopper } from '../common';
import { useAuth } from '../../context/AuthProvider';
import { checkUserRole } from '../../utility/userRoles';
import { UserRole } from '../../types/users';
import { useUserPortal } from '../../context/UserPortalContext';

type Props = Readonly<{
    executive: ProcurementExecutive;
}>;

function ProcurementExecutiveCard({ executive }: Props) {
    const { organisationSearch } = useOrganisationSearch();
    const { authUser } = useAuth();
    const isUserAdmin = checkUserRole(authUser, [
        UserRole.ADMIN,
        UserRole.SUPER_ADMIN,
    ]);
    const { setTeamsModal } = useUserPortal();

    const fullName = useMemo(() => {
        return `${executive.firstName} ${executive.lastName}`;
    }, [executive.firstName, executive.lastName]);

    const openAddProcurementManagerModal = () => {
        setTeamsModal({
            opened: true,
            movingStoreManager: false,
            addingStoreManager: false,
            addingProcurementManager: true,
            procurementExecutive: executive,
            storeManager: null,
        });
    };

    return (
        <Accordion.Item value={executive.id ?? ''} className="mb-3 group">
            <Accordion.Control
                style={{ borderLeft: '4px solid #00539F' }}
                className="h-10 border-l-4 border-[#00539F] hover:bg-primary-light/50"
            >
                <div className="flex items-center justify-between ">
                    <Text>
                        <Highlight
                            className="font-bold"
                            highlight={organisationSearch}
                        >
                            {fullName}
                        </Highlight>
                    </Text>
                    {isUserAdmin && (
                        <PropagationStopper>
                            <Button
                                variant="subtle"
                                className="text-primary rounded-md p-1 w-min h-min opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={openAddProcurementManagerModal}
                            >
                                <IconUserPlus />
                            </Button>
                        </PropagationStopper>
                    )}
                </div>
            </Accordion.Control>
            <Accordion.Panel className="p-0">
                <OpsLeaderCard personsRole="executive" person={executive} />
                {executive.managers?.length ? (
                    <ProcurementManagers managers={executive.managers ?? []} />
                ) : (
                    <div className="flex items-center justify-center h-20">
                        <p className="font-medium">
                            No Procurement Managers Found
                        </p>
                    </div>
                )}
            </Accordion.Panel>
        </Accordion.Item>
    );
}

export default ProcurementExecutiveCard;
